import type { Component } from "solid-js";
import { untrack } from "solid-js";
import InputModal from "../../../components/modal/InputModal";
import ApiService from "../../../services/ApiService";
import GenericFailureModal from "../../GenericFailureModal";
import { useTranslation } from "../../../state/i18n";
import { object } from "valibot";
import type FolderId from "../../../state/FolderId";
import type FormId from "../../../state/FormId";
import { folderReference } from "../../../utils/validation/idReferences";
import { SingleSelectInput } from "../../../components/menu/Select";
import { useModalManager } from "../../../components/modal/ModalManager";

type MoveFormModalProps = {
  folderId: FolderId;
  formId: FormId;
};

const moveFormSchema = object({
  toFolderId: folderReference(),
});

const MoveFormModal: Component<MoveFormModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const foldersQuery = ApiService.getFolders();

  return (
    <InputModal
      variant="default"
      title={t(($) => $.MOVE_FORM_MODAL_TITLE)}
      confirmLabel={t(($) => $.MOVE_FORM_MODAL_ACTIONS_MOVE)}
      cancelLabel={t(($) => $.MOVE_FORM_MODAL_ACTIONS_CANCEL)}
      onConfirm={(value) =>
        new Promise((resolve, reject) => {
          ApiService.moveForm().mutate(
            untrack(() => ({ fromFolderId: props.folderId, toFolderId: value.toFolderId, formId: props.formId })),
            {
              onSuccess: resolve,
              onError: (err) => {
                replace({
                  content: () => <GenericFailureModal title={t(($) => $.MOVE_FORM_FAILURE_MODAL_TITLE)} error={err} />,
                });
                reject(err);
              },
            },
          );
        })
      }
      schema={moveFormSchema}
      initialValue={{ toFolderId: undefined }}
    >
      {({ value, updateValue, error }) => (
        <div class="flex flex-col gap-6">
          <SingleSelectInput<FolderId>
            label={t(($) => $.MOVE_FORM_MODAL_INPUT_TO_FOLDER)}
            value={value("toFolderId")}
            onSelect={(value) => updateValue("toFolderId", value)}
            options={
              foldersQuery.data
                ?.filter((folder) => !folder.id.equals(props.folderId))
                .map((folder) => ({
                  variant: "icon",
                  value: folder.id,
                  label: folder.meta.name,
                  icon: { name: folder.meta.icon, type: "solid" },
                })) ?? []
            }
            errorMessage={error("toFolderId")}
            required
            placeholder={t(($) => $.MOVE_FORM_MODAL_INPUT_TO_FOLDER_PLACEHOLDER)}
            sameWidth
          />
        </div>
      )}
    </InputModal>
  );
};

export default MoveFormModal;
